/* eslint-disable object-curly-newline */
import { Modal, ModalContent, ModalOverlay, useToast } from '@chakra-ui/react';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ResolveIssueFormSchema, ResolveIssueFormValues } from './form-schema';
import { ResolveIssueFormBody } from './resolve-issue-form-body';
import { ResolveIssueConfirmation } from './resolve-issue-confirmation';
import { resolveActiveNotice } from '../../service';

export function ResolveIssueModal({
    propertyId,
    noticeId,
    isOpen,
    onClose,
}: {
    propertyId: number;
    noticeId: number;
    isOpen: boolean;
    onClose: () => void;
}) {
    const [screen, setScreen] = useState<'REQUEST' | 'CONFIRM'>('REQUEST');

    const toast = useToast();

    const queryClient = useQueryClient();

    const form = useForm<ResolveIssueFormValues>({
        resolver: zodResolver(ResolveIssueFormSchema),
        defaultValues: {
            resolveReason: '',
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
        setScreen('REQUEST');
    };

    const handleSubmit = () => {
        setScreen('CONFIRM');
    };

    const handleBack = () => {
        setScreen('REQUEST');
    };

    const mutation = useMutation({
        mutationFn: (data: {
            resolveReason: string; //
        }) => resolveActiveNotice(
            noticeId, //
            propertyId,
            data.resolveReason
        ),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['notices'],
            });

            void queryClient.invalidateQueries({
                queryKey: ['dashboard', 'tab', 'urgent-active-notices'],
            });

            toast({
                title: 'Resolve Success',
                description: 'Successfully resolved.', // TODO: change wording
                status: 'success',
                isClosable: true,
            });

            handleClose();
        },
        onError: (error) => {
            toast({
                title: 'Resolve Failed',
                description: error.message,
                status: 'error',
                isClosable: true,
            });
        },
    });

    const handleConfirm = (vals: ResolveIssueFormValues) => {
        mutation.mutate({ resolveReason: vals.resolveReason });
    };

    return (
        <Modal
            isOpen={isOpen}
            autoFocus={false}
            closeOnEsc={!mutation.isPending}
            closeOnOverlayClick={!mutation.isPending}
            onClose={handleClose}
            size="2xl"
        >
            <ModalOverlay />
            <ModalContent>
                <FormProvider {...form}>
                    {screen === 'REQUEST' ? (
                        <ResolveIssueFormBody
                            propertyId={propertyId}
                            onClose={handleClose}
                            onSubmit={handleSubmit}
                        />
                    ) : null}
                    {screen === 'CONFIRM' ? (
                        <ResolveIssueConfirmation
                            propertyId={propertyId}
                            onSubmit={handleConfirm}
                            onBack={handleBack}
                            isLoading={mutation.isPending}
                            error={mutation.error}
                        />
                    ) : null}
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}
